/**
 * 通用筛选弹窗组件
 * 
 * @example
 * ```tsx
 * import FilterModal, { type FilterField, type FilterCondition, MatchType } from '@/components/FilterModal'
 * 
 * // 定义筛选字段配置
 * const filterFields: FilterField[] = [
 *   // 字符串类型字段
 *   {
 *     key: 'name',
 *     label: '名称',
 *     valueType: 'string',
 *     placeholder: '请输入名称',
 *   },
 *   // 数字类型字段
 *   {
 *     key: 'age',
 *     label: '年龄',
 *     valueType: 'number',
 *     placeholder: '请输入年龄',
 *     inputNumberProps: {
 *       min: 0,
 *       max: 150,
 *     },
 *   },
 *   // 日期类型字段
 *   {
 *     key: 'birthday',
 *     label: '生日',
 *     valueType: 'date',
 *     placeholder: '请选择生日',
 *   },
 *   // 日期范围类型字段
 *   {
 *     key: 'dateRange',
 *     label: '日期范围',
 *     valueType: 'dateRange',
 *     placeholder: '请选择日期范围',
 *   },
 *   // 单选下拉框
 *   {
 *     key: 'status',
 *     label: '状态',
 *     valueType: 'select',
 *     placeholder: '请选择状态',
 *     options: [
 *       { value: 'active', label: '激活' },
 *       { value: 'inactive', label: '未激活' },
 *       { value: 'pending', label: '待处理' },
 *     ],
 *   },
 *   // 多选下拉框
 *   {
 *     key: 'tags',
 *     label: '标签',
 *     valueType: 'selectMultiple',
 *     placeholder: '请选择标签（可多选）',
 *     options: [
 *       { value: 'tag1', label: '标签1' },
 *       { value: 'tag2', label: '标签2' },
 *       { value: 'tag3', label: '标签3' },
 *     ],
 *   },
 * ]
 * 
 * // 在组件中使用
 * const MyComponent: React.FC = () => {
 *   const [filterVisible, setFilterVisible] = useState(false)
 * 
 *   // 处理搜索
 *   const handleSearch = (conditions: FilterCondition[]) => {
 *     console.log('筛选条件:', conditions)
 *     // conditions 示例:
 *     // [
 *     //   {
 *     //     field: 'name',
 *     //     matchType: MatchType.LIKE,
 *     //     value: '张三'
 *     //   },
 *     //   {
 *     //     field: 'age',
 *     //     matchType: MatchType.GREATER_THAN_OR_EQUAL,
 *     //     value: 18
 *     //   },
 *     //   {
 *     //     field: 'tags',
 *     //     matchType: MatchType.IN,
 *     //     value: ['tag1', 'tag2']  // 多选返回数组
 *     //   }
 *     // ]
 *     
 *     // 根据条件调用 API 或更新数据
 *     // fetchData(conditions)
 *   }
 * 
 *   // 处理重置
 *   const handleReset = () => {
 *     console.log('重置筛选条件')
 *   }
 * 
 *   return (
 *     <>
 *       <Button onClick={() => setFilterVisible(true)}>打开筛选</Button>
 *       
 *       <FilterModal
 *         visible={filterVisible}
 *         onVisibleChange={setFilterVisible}
 *         title="数据筛选"
 *         fields={filterFields}
 *         onSearch={handleSearch}
 *         onReset={handleReset}
 *         width={900}
 *         showReset={true}
 *         searchText="搜索"
 *         resetText="重置"
 *         cancelText="取消"
 *       />
 *     </>
 *   )
 * }
 * ```
 * 
 * @remarks
 * - 字段类型支持：string（文本输入）、number（数字输入）、date（日期选择）、dateRange（日期范围）、select（单选下拉）、selectMultiple（多选下拉）
 * - 匹配类型支持：等于、模糊匹配、不等于、大于、大于等于、小于、小于等于、包含、不包含
 * - 相同字段不能在不同行重复选择
 * - 添加新行前需要确保所有行的字段都已选择
 * - 多选下拉框返回的值为数组类型，匹配类型仅支持"包含"和"不包含"
 */

import React, { useState, useEffect } from 'react'
import {
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Divider,
  message,
} from 'antd'
import {
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
} from '@ant-design/icons'
import type { FilterModalProps, FilterCondition, FilterField } from './types'
import { MatchType, MATCH_TYPE_OPTIONS, FieldValueType } from './types'

const { RangePicker } = DatePicker

/**
 * 通用筛选弹窗组件
 */
const FilterModal: React.FC<FilterModalProps> = ({
  visible,
  onVisibleChange,
  title = '筛选条件',
  fields,
  onSearch,
  onReset,
  width = 800,
  showReset = true,
  searchText = '搜索',
  resetText = '重置',
  cancelText = '取消',
}) => {
  const [form] = Form.useForm()
  const [filterRows, setFilterRows] = useState<Array<{ id: string; field: string; matchType: MatchType; value: any }>>([
    { id: '1', field: '', matchType: MatchType.EQUAL, value: null },
  ])
  // 保存筛选条件状态，用于重新打开弹窗时恢复
  const [savedFilterData, setSavedFilterData] = useState<{
    rows: Array<{ id: string; field: string; matchType: MatchType; value: any }>
    formValues: Record<string, any>
  } | null>(null)

  // 当弹窗打开时，恢复之前保存的筛选条件或重置表单
  useEffect(() => {
    if (visible) {
      if (savedFilterData) {
        // 恢复之前保存的筛选条件
        setFilterRows(savedFilterData.rows)
        form.setFieldsValue(savedFilterData.formValues)
      } else {
        // 首次打开，重置表单
        setFilterRows([{ id: '1', field: '', matchType: MatchType.EQUAL, value: null }])
        form.resetFields()
      }
    }
  }, [visible, form, savedFilterData])

  /**
   * 添加筛选行
   */
  const handleAddRow = () => {
    // 检查所有行的字段是否都已选中
    const formValues = form.getFieldsValue()
    const hasEmptyField = filterRows.some((row) => {
      const fieldValue = formValues[`field_${row.id}`]
      return !fieldValue || fieldValue === undefined || fieldValue === null || fieldValue === ''
    })

    if (hasEmptyField) {
      message.warning('请先为所有行的字段选择值，才能添加新的筛选条件')
      return
    }

    const newId = `${Date.now()}`
    setFilterRows([
      ...filterRows,
      { id: newId, field: '', matchType: MatchType.EQUAL, value: null },
    ])
  }

  /**
   * 删除筛选行
   */
  const handleDeleteRow = (id: string) => {
    if (filterRows.length > 1) {
      setFilterRows(filterRows.filter((row) => row.id !== id))
      // 删除对应的表单字段
      form.setFieldsValue({
        [`field_${id}`]: undefined,
        [`matchType_${id}`]: undefined,
        [`value_${id}`]: undefined,
      })
    }
  }

  /**
   * 获取字段配置
   */
  const getFieldConfig = (fieldKey: string): FilterField | undefined => {
    return fields.find((field) => field.key === fieldKey)
  }

  /**
   * 获取当前行可用的字段列表（排除其他行已选中的字段）
   */
  const getAvailableFields = (currentRowId: string): FilterField[] => {
    const formValues = form.getFieldsValue()
    // 获取所有其他行已选中的字段
    const selectedFields: string[] = filterRows
      .filter((r) => r.id !== currentRowId)
      .map((r) => formValues[`field_${r.id}`] as string)
      .filter(
        (field): field is string =>
          field !== undefined && field !== null && field !== '',
      )

    // 返回未被其他行选中的字段，以及当前行已选中的字段（允许当前行继续使用）
    return fields.filter((field) => {
      // 如果字段被禁用，直接排除
      if (field.disabled) {
        return false
      }
      // 如果字段已被其他行选中，排除
      if (selectedFields.includes(field.key)) {
        return false
      }
      // 其他情况都可用
      return true
    })
  }

  /**
   * 渲染值输入组件
   */
  const renderValueInput = (
    rowId: string,
    fieldKey: string,
    valueType: FieldValueType = 'string',
    placeholder?: string,
    inputNumberProps?: any,
    datePickerProps?: any,
    options?: Array<{ value: string | number; label: string; disabled?: boolean }>,
  ) => {
    const fieldName = `value_${rowId}`

    switch (valueType) {
      case 'number':
        return (
          <Form.Item name={fieldName} noStyle>
            <InputNumber
              style={{ width: '100%', borderRadius: 6 }}
              placeholder={placeholder || '请输入数值'}
              size="large"
              {...inputNumberProps}
            />
          </Form.Item>
        )
      case 'date':
        return (
          <Form.Item name={fieldName} noStyle>
            <DatePicker
              style={{ width: '100%', borderRadius: 6 }}
              placeholder={placeholder || '请选择日期'}
              size="large"
              {...datePickerProps}
            />
          </Form.Item>
        )
      case 'dateRange':
        return (
          <Form.Item name={fieldName} noStyle>
            <RangePicker
              style={{ width: '100%', borderRadius: 6 }}
              placeholder={[placeholder || '开始日期', placeholder || '结束日期']}
              size="large"
              {...datePickerProps}
            />
          </Form.Item>
        )
      case 'select':
        return (
          <Form.Item name={fieldName} noStyle>
            <Select
              style={{ width: '100%', borderRadius: 6 }}
              placeholder={placeholder || '请选择'}
              size="large"
              allowClear
            >
              {options?.map((option) => (
                <Select.Option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )
      case 'selectMultiple':
        return (
          <Form.Item name={fieldName} noStyle>
            <Select
              mode="multiple"
              style={{ width: '100%', borderRadius: 6 }}
              placeholder={placeholder || '请选择（可多选）'}
              size="large"
              allowClear
              maxTagCount="responsive"
            >
              {options?.map((option) => (
                <Select.Option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )
      default:
        return (
          <Form.Item name={fieldName} noStyle>
            <Input
              placeholder={placeholder || '请输入值'}
              allowClear
              size="large"
              style={{ borderRadius: 6 }}
            />
          </Form.Item>
        )
    }
  }

  /**
   * 处理搜索
   */
  const handleSearch = async () => {
    // 先获取当前表单的所有值
    const currentValues = form.getFieldsValue()
    
    // 先检查哪些行有有效的筛选条件（字段已选择）
    const rowsWithField = filterRows.filter((row) => {
      const fieldKey = currentValues[`field_${row.id}`]
      return fieldKey && fieldKey.trim() !== ''
    })

    // 如果没有一行选择了字段，直接执行搜索（返回空数组）
    if (rowsWithField.length === 0) {
      // 保存筛选条件状态，用于重新打开弹窗时恢复
      setSavedFilterData({
        rows: filterRows,
        formValues: currentValues,
      })

      onSearch([])
      onVisibleChange(false)
      return
    }

    // 如果有行选择了字段，只验证这些行的值字段（字段和匹配类型已经有值，不需要验证）
    // 这样可以确保获取到最新值，同时避免验证未选择字段的行
    const fieldsToValidate: string[] = []
    rowsWithField.forEach((row) => {
      // 只验证值字段，因为字段和匹配类型已经选择了
      fieldsToValidate.push(`value_${row.id}`)
    })

    try {
      // 验证值字段，验证成功后会强制同步表单值
      await form.validateFields(fieldsToValidate)
      
      // 验证成功后，获取所有字段的最新值（此时值已经同步）
      const finalFormValues = form.getFieldsValue()
      
      const conditions: FilterCondition[] = []

      filterRows.forEach((row) => {
        const fieldKey = finalFormValues[`field_${row.id}`]
        const matchType = finalFormValues[`matchType_${row.id}`]
        const value = finalFormValues[`value_${row.id}`]

        // 只添加有值的筛选条件
        if (fieldKey && matchType && value !== undefined && value !== null && value !== '') {
          const fieldConfig = getFieldConfig(fieldKey)
          let processedValue: string | number | [string, string] | (string | number)[] = value

          // 处理日期范围
          if (fieldConfig?.valueType === 'dateRange' && Array.isArray(value)) {
            processedValue = [
              value[0]?.format('YYYY-MM-DD') || '',
              value[1]?.format('YYYY-MM-DD') || '',
            ]
          } else if (fieldConfig?.valueType === 'date' && value) {
            // 处理单个日期
            processedValue = value.format('YYYY-MM-DD')
          } else if (fieldConfig?.valueType === 'number') {
            // 确保数字类型
            processedValue = Number(value)
          } else if (fieldConfig?.valueType === 'selectMultiple' && Array.isArray(value)) {
            // 多选下拉框的值已经是数组，直接使用
            processedValue = value
          }

          conditions.push({
            field: fieldKey,
            matchType: matchType as MatchType,
            value: processedValue,
          })
        }
      })

      // 保存筛选条件状态，用于重新打开弹窗时恢复
      setSavedFilterData({
        rows: filterRows,
        formValues: finalFormValues,
      })
      console.log(conditions)
      onSearch(conditions)
      onVisibleChange(false)
    } catch (error) {
      // 验证失败时，不执行搜索，让表单显示错误信息
      console.error('表单验证失败:', error)
    }
  }

  /**
   * 处理重置
   */
  const handleReset = () => {
    setFilterRows([{ id: '1', field: '', matchType: MatchType.EQUAL, value: null }])
    form.resetFields()
    // 清空保存的筛选条件
    setSavedFilterData(null)
    if (onReset) {
      onReset()
    }
  }

  /**
   * 处理取消
   */
  const handleCancel = () => {
    onVisibleChange(false)
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FilterOutlined style={{ color: '#1890ff', fontSize: 18 }} />
          <span style={{ fontSize: 16, fontWeight: 600 }}>{title}</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={width}
      destroyOnClose
      styles={{
        body: {
          padding: '24px 24px 0 24px',
          maxHeight: 'calc(70vh - 80px)',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          scrollbarColor: 'transparent transparent'
        },
      }}
      footer={
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #f0f0f0',
            background: '#fff',
            textAlign: 'right',
          }}
        >
          <Space size="middle">
            {showReset && (
              <Button
                onClick={handleReset}
                icon={<ReloadOutlined />}
                size="large"
                style={{
                  borderRadius: 6,
                  minWidth: 100,
                }}
              >
                {resetText}
              </Button>
            )}
            <Button
              onClick={handleCancel}
              size="large"
              style={{
                borderRadius: 6,
                minWidth: 100,
              }}
            >
              {cancelText}
            </Button>
            <Button
              type="primary"
              onClick={handleSearch}
              icon={<SearchOutlined />}
              size="large"
              style={{
                borderRadius: 6,
                minWidth: 100,
                boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)',
              }}
            >
              {searchText}
            </Button>
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        {/* 标题行 */}
        <div
          style={{
            marginBottom: 12,
            padding: '12px 20px',
            background: '#f5f5f5',
            border: '1px solid #e8e8e8',
            borderRadius: 8,
          }}
        >
          <Row gutter={[16, 0]}>
            <Col span={7}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#262626' }}>字段</span>
            </Col>
            <Col span={5}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#262626' }}>匹配类型</span>
            </Col>
            <Col span={10}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#262626' }}>值</span>
            </Col>
            <Col span={2}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#262626' }}></span>
            </Col>
          </Row>
        </div>

        {/* 筛选条件行 */}
        <Form.Item
          noStyle
          shouldUpdate={(prevValues: any, currentValues: any) => {
            // 当任何行的字段值发生变化时，重新渲染所有筛选行
            return filterRows.some(
              (r) =>
                prevValues[`field_${r.id}`] !== currentValues[`field_${r.id}`],
            )
          }}
        >
          {() => (
            <div style={{ marginBottom: 16 }}>
              {filterRows.map((row, index) => {
                const fieldValue = form.getFieldValue(`field_${row.id}`)
                const matchTypeValue = form.getFieldValue(`matchType_${row.id}`)
                const fieldConfig = fieldValue ? getFieldConfig(fieldValue) : undefined
                const availableFields = getAvailableFields(row.id)
                const finalFields = [...availableFields]
                // 如果当前行已选中字段，确保它在列表中
                if (fieldValue) {
                  const currentField = fields.find((f) => f.key === fieldValue)
                  if (
                    currentField &&
                    !finalFields.find((f) => f.key === fieldValue)
                  ) {
                    finalFields.push(currentField)
                  }
                }

                // 获取可用的匹配类型选项
                const availableMatchTypes =
                  fieldConfig?.valueType === 'selectMultiple'
                    ? MATCH_TYPE_OPTIONS.filter(
                        (option) =>
                          option.value === MatchType.IN ||
                          option.value === MatchType.NOT_IN,
                      )
                    : MATCH_TYPE_OPTIONS

                // 如果当前匹配类型不在可用列表中，重置为等于
                if (
                  matchTypeValue &&
                  !availableMatchTypes.find((opt) => opt.value === matchTypeValue)
                ) {
                  form.setFieldsValue({
                    [`matchType_${row.id}`]: MatchType.EQUAL,
                  })
                }

                return (
              <div
                key={row.id}
                style={{
                  marginBottom: index < filterRows.length - 1 ? 16 : 0,
                  padding: '20px',
                  background: '#fafafa',
                  border: '1px solid #e8e8e8',
                  borderRadius: 8,
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)'
                  e.currentTarget.style.borderColor = '#d9d9d9'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.04)'
                  e.currentTarget.style.borderColor = '#e8e8e8'
                }}
              >
                <Row gutter={[16, 0]} align="middle">
                  <Col span={7}>
                    <Form.Item
                      name={`field_${row.id}`}
                      rules={[{ required: true, message: '请选择字段' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder="请选择字段"
                        allowClear
                        style={{ borderRadius: 6 }}
                        size="large"
                      >
                        {finalFields.map((field) => (
                          <Select.Option
                            key={field.key}
                            value={field.key}
                            disabled={field.disabled}
                          >
                            {field.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={5}>
                    <Form.Item
                      name={`matchType_${row.id}`}
                      rules={[{ required: true, message: '请选择匹配类型' }]}
                      initialValue={MatchType.EQUAL}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder="请选择匹配类型"
                        style={{ borderRadius: 6 }}
                        size="large"
                      >
                        {availableMatchTypes.map((option) => (
                          <Select.Option key={option.value} value={option.value}>
                            {option.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues[`field_${row.id}`] !== currentValues[`field_${row.id}`]
                      }
                    >
                      {({ getFieldValue }) => {
                        const currentFieldValue = getFieldValue(`field_${row.id}`)
                        const currentFieldConfig = currentFieldValue
                          ? getFieldConfig(currentFieldValue)
                          : undefined

                        // 当字段变化时，清空值
                        if (currentFieldValue !== fieldValue) {
                          form.setFieldsValue({
                            [`value_${row.id}`]: undefined,
                          })
                        }

                        return (
                          <Form.Item
                            name={`value_${row.id}`}
                            rules={[{ required: true, message: '请输入值' }]}
                            style={{ marginBottom: 0 }}
                          >
                            {currentFieldConfig
                              ? renderValueInput(
                                  row.id,
                                  currentFieldValue,
                                  currentFieldConfig.valueType,
                                  currentFieldConfig.placeholder,
                                  currentFieldConfig.inputNumberProps,
                                  currentFieldConfig.datePickerProps,
                                  currentFieldConfig.options,
                                )
                              : (
                                <Input
                                  placeholder="请先选择字段"
                                  disabled
                                  size="large"
                                  style={{ borderRadius: 6 }}
                                />
                              )}
                          </Form.Item>
                        )
                      }}
                    </Form.Item>
                  </Col>
                  <Col span={1}>
                    {filterRows.length > 1 && (
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteRow(row.id)}
                        style={{
                          width: 28,
                          height: 28,
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          background: '#fff',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.1)'
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 77, 79, 0.3)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)'
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                    )}
                  </Col>
                </Row>
              </div>
                )
              })}
            </div>
          )}
        </Form.Item>

        <div style={{ marginBottom: 24 }}>
          <Button
            type="dashed"
            onClick={handleAddRow}
            block
            icon={<PlusOutlined />}
            size="large"
            style={{
              borderRadius: 6,
              height: 40,
              borderStyle: 'dashed',
              borderColor: '#d9d9d9',
              color: '#595959',
              fontWeight: 500,
              transition: 'all 0.3s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#1890ff'
              e.currentTarget.style.color = '#1890ff'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d9d9d9'
              e.currentTarget.style.color = '#595959'
            }}
          >
            添加筛选条件
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

export default FilterModal
